import React, { useState } from 'react';
import { Link } from 'react-router';
import { z } from 'zod';

import { Form, TextInputField } from '@repo/ui/Form';
import { Button } from '@repo/ui/Button';
import { registerInput, useZodForm } from '@repo/hooks/useZodForm';
import { createAuthClient, AuthClientError } from '../api/authClient';

const forgotPasswordSchema = z.object({
  email: z.string().email({ message: 'Please enter a valid email address.' }),
});

type ForgotPasswordParams = z.infer<typeof forgotPasswordSchema>;

interface ForgotPasswordFormProps {
  passwordLoginEndpoint: string;
}

export const ForgotPasswordForm = ({ passwordLoginEndpoint }: ForgotPasswordFormProps) => {
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const authClient = createAuthClient({ passwordLoginEndpoint });

  const form = useZodForm({
    schema: forgotPasswordSchema,
    async onSubmit(params: ForgotPasswordParams) {
      setError(null);
      try {
        await authClient.startPasswordReset(params.email);
        setSubmitted(true);
      } catch (err) {
        if (err instanceof AuthClientError) {
          setError(err.message);
        } else {
          setError('An unexpected error occurred. Please try again.');
        }
      }
    },
  });

  if (submitted) {
    return (
      <>
        <div className="flex flex-col items-center mb-5 w-full sm:mb-8">
          <h1 className="mb-2.5 sm:mb-5 text-4xl font-bold sm:text-5xl dark:text-blue-100">
            Check Your Email
          </h1>
        </div>

        <div className="flex flex-col items-center px-5 py-8 w-full rounded-xl border xs:px-10 sm:px-16 bg-grey-200 border-grey-400 dark:bg-blue-1000 dark:border-grey-700">
          <p className="mb-6 text-center dark:text-blue-100">
            If an account exists with that email address, you will receive a password reset link
            shortly.
          </p>
          <Link to="/login">
            <Button variant="secondary">Back to Login</Button>
          </Link>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="flex flex-col items-center mb-5 w-full sm:mb-8">
        <h1 className="mb-2.5 sm:mb-5 text-4xl font-bold sm:text-5xl dark:text-blue-100">
          Forgot Password
        </h1>
        <p className="text-center dark:text-blue-100">
          Enter your email address and we&apos;ll send you a link to reset your password.
        </p>
      </div>

      <div className="flex flex-col items-center px-5 py-8 w-full rounded-xl border xs:px-10 sm:px-16 bg-grey-200 border-grey-400 dark:bg-blue-1000 dark:border-grey-700">
        <Form className="flex flex-col gap-6 items-center w-full" {...form}>
          <Form.Submitting>Sending reset link...</Form.Submitting>
          {error && <Form.Error autoClose={false}>{error}</Form.Error>}

          <TextInputField label="Email" type="email" {...registerInput(form, 'email')} />

          <div className="flex flex-col items-center mt-4 sm:col-span-2">
            <Button size="md" type="submit" loading={form.formState.isSubmitting}>
              Send Reset Link
            </Button>
            <Link to="/login">
              <small className="dark:text-blue-300 text-grey-600">Back to Login</small>
            </Link>
          </div>
        </Form>
      </div>
    </>
  );
};
